/**
 * Input Component
 * 
 * A powerful, customizable input component built on top of Material UI TextField.
 * Provides consistent styling with HAAT brand colors and enhanced features.
 * 
 * @example
 * // Basic
 * <Input label="Email" type="email" />
 * 
 * @example
 * // With error
 * <Input label="Username" errorMessage="Username is required" />
 * 
 * @example
 * // Password with toggle
 * <Input label="Password" type="password" showPasswordToggle />
 * 
 * @example
 * // With icons
 * <Input label="Search" startIcon={<Search />} placeholder="Search..." />
 * 
 * @example
 * // Multiline
 * <Input label="Description" multiline rows={4} />
 */

import React, { useState, forwardRef } from 'react';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import IconButton from '@mui/material/IconButton';
import CircularProgress from '@mui/material/CircularProgress';
import { Eye, EyeOff } from 'lucide-react';
import { InputProps, InputSize } from './Input.types';

// =============================================================================
// SIZE MAPPING
// =============================================================================

/**
 * Maps our size variants to MUI sizes and custom heights
 */
const sizeConfig: Record<InputSize, { muiSize: 'small' | 'medium'; height: string }> = {
  small: { muiSize: 'small', height: '36px' },
  medium: { muiSize: 'medium', height: '44px' },
  large: { muiSize: 'medium', height: '52px' },
};

// =============================================================================
// COMPONENT
// =============================================================================

const Input = forwardRef<HTMLInputElement, InputProps>(
  (
    {
      // Visual styling
      variant = 'outlined',
      size = 'medium',
      fullWidth = true,
      rounded = true,

      // Label & helper
      label,
      hideLabel = false,
      placeholder,
      helperText,

      // Validation
      error = false,
      errorMessage,
      required = false,

      // Input type & behavior
      type = 'text',
      showPasswordToggle = false,
      multiline = false,
      rows = 4,
      minRows,
      maxRows,

      // Adornments
      startIcon,
      endIcon,
      prefix,
      suffix,

      // State
      disabled = false,
      readOnly = false,
      loading = false,

      // Custom styling
      width,
      className,
      sx,

      // Rest of MUI props
      ...muiProps
    },
    ref
  ) => {
    // =========================================================================
    // STATE
    // =========================================================================

    const [showPassword, setShowPassword] = useState(false);

    // =========================================================================
    // COMPUTED VALUES
    // =========================================================================

    // Determine if there's an error
    const hasError = error || !!errorMessage;
    const displayHelperText = errorMessage || helperText;

    // Get size configuration
    const { muiSize, height } = sizeConfig[size];

    // Determine actual input type (for password toggle)
    const actualType = type === 'password' && showPassword ? 'text' : type;

    // =========================================================================
    // HANDLERS
    // =========================================================================

    const handleTogglePassword = () => {
      setShowPassword((prev) => !prev);
    };

    // =========================================================================
    // BUILD ADORNMENTS
    // =========================================================================

    const buildStartAdornment = () => {
      if (!startIcon && !prefix) return undefined;

      return (
        <InputAdornment position="start">
          {prefix && (
            <span className="text-gray-500 text-sm font-medium">{prefix}</span>
          )}
          {startIcon && (
            <span className="text-gray-400">{startIcon}</span>
          )}
        </InputAdornment>
      );
    };

    const buildEndAdornment = () => {
      // Priority: loading > password toggle > custom endIcon/suffix
      if (loading) {
        return (
          <InputAdornment position="end">
            <CircularProgress size={20} className="text-primary-500" />
          </InputAdornment>
        );
      }

      if (type === 'password' && showPasswordToggle) {
        return (
          <InputAdornment position="end">
            <IconButton
              aria-label={showPassword ? 'Hide password' : 'Show password'}
              onClick={handleTogglePassword}
              edge="end"
              size="small"
              tabIndex={-1}
            >
              {showPassword ? (
                <EyeOff size={18} className="text-gray-400" />
              ) : (
                <Eye size={18} className="text-gray-400" />
              )}
            </IconButton>
          </InputAdornment>
        );
      }

      if (!endIcon && !suffix) return undefined;

      return (
        <InputAdornment position="end">
          {endIcon && (
            <span className="text-gray-400">{endIcon}</span>
          )}
          {suffix && (
            <span className="text-gray-500 text-sm font-medium">{suffix}</span>
          )}
        </InputAdornment>
      );
    };

    // =========================================================================
    // CUSTOM STYLES
    // =========================================================================

    const customStyles = {
      width: width || (fullWidth ? '100%' : 'auto'),

      // Root styles
      '& .MuiOutlinedInput-root': {
        borderRadius: rounded ? '8px' : '4px',
        minHeight: multiline ? 'auto' : height,
        backgroundColor: variant === 'filled' ? 'rgba(0, 0, 0, 0.04)' : 'transparent',
        transition: 'all 0.2s ease-in-out',

        '& fieldset': {
          borderColor: hasError ? '#c00035' : '#e5e7eb',
          transition: 'border-color 0.2s ease-in-out',
        },

        '&:hover fieldset': {
          borderColor: hasError ? '#c00035' : '#9ca3af',
        },

        '&.Mui-focused fieldset': {
          borderColor: hasError ? '#c00035' : 'rgb(var(--color-primary-500))',
          borderWidth: '2px',
        },

        '&.Mui-disabled': {
          backgroundColor: '#f9fafb',
          '& fieldset': {
            borderColor: '#e5e7eb',
          },
        },
      },

      // Label styles
      '& .MuiInputLabel-root': {
        color: hasError ? '#c00035' : '#6b7280',

        '&.Mui-focused': {
          color: hasError ? '#c00035' : 'rgb(var(--color-primary-500))',
        },
      },

      // Input styles
      '& .MuiInputBase-input': {
        fontSize: size === 'small' ? '0.875rem' : '1rem',
        padding: multiline ? '12px 14px' : undefined,

        '&::placeholder': {
          color: '#9ca3af',
          opacity: 1,
        },
      },

      // Helper text styles
      '& .MuiFormHelperText-root': {
        marginLeft: '2px',
        marginTop: '6px',
        fontSize: '0.75rem',

        '&.Mui-error': {
          color: '#c00035',
        },
      },

      // Filled variant specific
      ...(variant === 'filled' && {
        '& .MuiFilledInput-root': {
          borderRadius: rounded ? '8px 8px 0 0' : '4px 4px 0 0',
          backgroundColor: 'rgba(0, 0, 0, 0.04)',

          '&:hover': {
            backgroundColor: 'rgba(0, 0, 0, 0.06)',
          },

          '&.Mui-focused': {
            backgroundColor: 'rgba(0, 0, 0, 0.06)',
          },
        },
      }),

      // Standard variant specific
      ...(variant === 'standard' && {
        '& .MuiInput-underline': {
          '&:before': {
            borderBottomColor: hasError ? '#c00035' : '#e5e7eb',
          },
          '&:hover:not(.Mui-disabled):before': {
            borderBottomColor: hasError ? '#c00035' : '#9ca3af',
          },
          '&:after': {
            borderBottomColor: hasError ? '#c00035' : 'rgb(var(--color-primary-500))',
          },
        },
      }),

      // Merge with custom sx
      ...sx,
    };

    // =========================================================================
    // RENDER
    // =========================================================================

    return (
      <TextField
        ref={ref}
        variant={variant}
        size={muiSize}
        fullWidth={fullWidth}
        label={hideLabel ? undefined : label}
        placeholder={placeholder}
        helperText={displayHelperText}
        error={hasError}
        required={required}
        type={actualType}
        multiline={multiline}
        rows={multiline ? rows : undefined}
        minRows={multiline ? minRows : undefined}
        maxRows={multiline ? maxRows : undefined}
        disabled={disabled || loading}
        InputProps={{
          readOnly,
          startAdornment: buildStartAdornment(),
          endAdornment: buildEndAdornment(),
          'aria-label': hideLabel ? label : undefined,
        }}
        className={className}
        sx={customStyles}
        {...muiProps}
      />
    );
  }
);

// Display name for debugging
Input.displayName = 'Input';

export default Input;


